import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCollectionSync } from '../../../hooks/useCollectionSync';

function buildSearchText(record) {
  if (!record || typeof record !== 'object') return '';

  const safe = {};
  Object.entries(record).forEach(([key, value]) => {
    if (key === 'raw') return;
    safe[key] = value;
  });

  return JSON.stringify(safe).toLowerCase();
}

function matchesSearch(record, query) {
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return true;
  return buildSearchText(record).includes(q);
}

export function useAnimalHealthRecords(api, searchQuery = '') {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadRecords = useCallback(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    api.listAll({ per_page: 1000 })
      .then((response) => {
        if (!mounted) return;
        setRecords(Array.isArray(response?.items) ? response.items : []);
      })
      .catch((err) => {
        if (!mounted) return;
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Unable to load records.';
        setRecords([]);
        setError(message);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [api]);

  useEffect(() => {
    const cancel = loadRecords();
    return () => {
      if (typeof cancel === 'function') cancel();
    };
  }, [loadRecords]);

  useCollectionSync(api.collectionKey, () => {
    loadRecords();
  });

  const filteredRecords = useMemo(
    () => records.filter((record) => matchesSearch(record, searchQuery)),
    [records, searchQuery]
  );

  return {
    records,
    filteredRecords,
    loading,
    error,
  };
}
